import { calculateXScore, calculateYScore } from "@utils/helpers";
import { isEmptyObject } from "jquery";
import { createSelector } from "reselect";
import { isNullOrUndefined } from "utils/helpers";
import { CustomizationColors } from "shared/ui-basics";

export const insightsHorizontalBarDataSelector = createSelector(
	(state) => ({
		currentData: state.analytics?.journeyTrend?.currentData,
	}),
	(state, props) => props,
	({ currentData }, props) => {
		const { type } = props;
		const { gainInsights, painInsights } = currentData;
		const insights = type === "gain" ? gainInsights : painInsights;
		const items = Object.entries(insights).map(([id, value]) => ({
			id: id,
			name: value.name,
			value: value.value,
			relatedItems: value.cjms,
		}));

		// Sort by count in descending order
		items.sort((a, b) => b.value - a.value);

		// Return first n items
		return items.slice(0, 5);
	}
);

export const wordCloudDataSelector = createSelector(
	(state) => ({
		currentData: state.analytics?.journeyTrend?.currentData,
	}),
	(state, props) => props,
	({ currentData }, props) => {
		const { type } = props;
		const { gainInsights, painInsights } = currentData;
		const insights = type === "gain" ? gainInsights : painInsights;
		const wordFrequency = new Map();

		for (const insight of Object.values(insights)) {
			// Process each word in the wordCloud
			for (const wordObj of insight.wordCloud) {
				const word = wordObj.text;
				const totalFrequency = insight.value * wordObj.value;

				// Update the frequency in the map
				if (wordFrequency.has(word)) {
					wordFrequency.set(word, wordFrequency.get(word) + totalFrequency);
				} else {
					wordFrequency.set(word, totalFrequency);
				}
			}
		}

		const wordList = Array.from(wordFrequency.entries())
			.map(([text, value]) => ({ text, value }))
			.sort((a, b) => b.value - a.value);

		// Return the top 50 (or fewer if there are fewer than 50 unique words)
		return wordList.slice(0, 50);
	}
);

export const entitiesByJourneyDataSelector = createSelector(
	(state) => ({
		selectedCjms: state.analytics?.journeyTrend?.selectedFilterOptions?.cjms,
		currentData: state.analytics?.journeyTrend?.currentData,
	}),
	(state, props) => props,
	({ selectedCjms, currentData }, props) => {
		const { entity } = props;
		const linkedIdsAttribute = entity + "Ids";
		const allItemsAttribute = entity === "opportunity" ? "opportunities" : entity === "solution" ? "solutions" : "actions";
		const allItems = currentData[allItemsAttribute];
		const allItemIds = allItems.map((it) => it?.id);

		const formatedData = [];
		selectedCjms?.forEach((cjm) => {
			const linkedIds = cjm[linkedIdsAttribute];
			if (linkedIds?.length > 0) {
				const res = [];
				linkedIds.forEach((linkedId) => {
					if (allItemIds.includes(linkedId)) res.push(linkedId);
				});
				if (res?.length > 0) formatedData.push({ name: cjm?.name, value: res?.length, relatedItemsIds: res });
			}
		});

		const sortedData = formatedData?.sort((a, b) => b.value - a.value)?.slice(0, 5);

		const memo = {};
		return sortedData?.map((data) => {
			const relatedItems = [];
			data.relatedItemsIds?.forEach((relatedItemId) => {
				if (!(relatedItemId in memo)) {
					const relatedItem = currentData[allItemsAttribute]?.find((item) => item.id === relatedItemId);
					if (!isEmptyObject(relatedItem)) {
						memo[relatedItemId] = true;
						relatedItems.push(relatedItem);
					}
				}
			});
			return { ...data, relatedItems: [...relatedItems] };
		});
	}
);

export const mapPerStatusDataSelector = createSelector(
	(state) => ({
		selectedCjms: state.analytics?.journeyTrend?.selectedFilterOptions?.cjms,
		statuses: state.libraries?.status?.customerJourney,
	}),
	(state, props) => props,
	({ selectedCjms, statuses }, props) => {
		const memory = {};
		const formatedData = [];

		selectedCjms?.forEach((cjm) => {
			if (!memory[cjm?.statusId]) memory[cjm?.statusId] = [cjm];
			else memory[cjm?.statusId].push(cjm);
		});

		//Transform the counter into %
		Object.keys(memory)?.forEach((statusId) => {
			const status = statuses?.find((s) => s?.id === statusId);
			formatedData?.push({
				statusId,
				statusName: status?.name || props.t("NO_VALUE"),
				color: CustomizationColors[status?.colorCode]?.backgroundColor || status?.colorCode || "#F3F4F6",
				value: (memory[statusId].length / selectedCjms?.length) * 100,
				maps: memory[statusId],
			});
		});

		return formatedData;
	}
);

export const opportunityAnalysisDataSelector = createSelector(
	(state) => ({
		opportunities: state.analytics?.journeyTrend?.currentData?.opportunities,
	}),
	(state, props) => props,
	({ opportunities }, props) => {
		const memory = {};
		const formatedData = [];
		const oppIds = [];

		opportunities?.forEach((opp) => {
			if (!oppIds?.includes(opp?.id)) {
				oppIds.push(opp?.id);
				const x = opp?.businessRatingValue;
				const y = opp?.customerRatingValue;
				if (!isNullOrUndefined(x) && !isNullOrUndefined(y)) {
					let strCoordinates = x + "|" + y;
					if (strCoordinates in memory) memory[strCoordinates].push(opp);
					else memory[strCoordinates] = [opp];
				}
			}
		});

		Object.keys(memory)?.forEach((strCoordinate) => {
			const coordinates = strCoordinate.split("|");
			formatedData.push({ x: coordinates[0], y: coordinates[1], opportunities: memory[strCoordinate] });
		});

		return formatedData;
	}
);

export const solutionAnalysisDataSelector = createSelector(
	(state) => ({
		solutions: state.analytics?.journeyTrend?.currentData?.solutions,
	}),
	(state, props) => props,
	({ solutions }, props) => {
		const memory = {};
		const finalData = [];

		solutions?.forEach((solution) => {
			const strCoordinates = calculateXScore(solution) + "|" + calculateYScore(solution);
			if (strCoordinates in memory) memory[strCoordinates].push(solution);
			else memory[strCoordinates] = [solution];
		});

		Object.keys(memory)?.forEach((strCoordinate) => {
			const coordinates = strCoordinate.split("|");
			finalData.push({ x: coordinates[0], y: coordinates[1], solutions: memory[strCoordinate] });
		});

		return finalData;
	}
);

export const topTenWithLabelsDataSelector = createSelector(
	(state) => ({
		tags: state.analytics?.journeyTrend?.currentData?.tags,
		groups: state.analytics?.journeyTrend?.currentData?.groups,
	}),
	(state, props) => props,
	({ tags, groups }, props) => {
		const { entity } = props;
		const records = entity === "tag" ? tags : groups;
		const result = new Map();

		for (const rec of records) {
			const totalUsage = (rec?.personaIds?.length || 0) + (rec?.cjmIds?.length || 0) + (rec?.programIds?.length || 0);

			result.set(rec.id, {
				id: rec.id,
				name: rec.name,
				value: totalUsage,
			});
		}

		const tagList = Array.from(result.values()).sort((a, b) => b.value - a.value);
		return tagList.slice(0, 10);
	}
);

export const topNTouchpointsDataSelector = createSelector(
	(state) => ({
		selectedCjms: state.analytics?.journeyTrend?.selectedFilterOptions?.cjms,
		currentTouchpoints: state.analytics?.journeyTrend?.currentData.touchpoints,
	}),
	(state, props) => props,
	({ selectedCjms, currentTouchpoints }, props) => {
		const counter = {};

		// Create a Counter object, tpId: numberOfOccurances
		selectedCjms?.forEach((map) => {
			map.touchpointIds?.forEach((touchpointId) => {
				const currentTp = currentTouchpoints?.find((tp) => tp.id === touchpointId);
				if (currentTp) {
					if (!counter[touchpointId]) counter[touchpointId] = { ...currentTp, value: 1, mapIds: new Set([map?.id]) };
					else counter[touchpointId] = { ...counter[touchpointId], value: counter[touchpointId].value + 1, mapIds: counter[touchpointId].mapIds.add(map?.id) };
				}
			});
		});

		//Transform the counter into a list
		const touchpoints = Object.values(counter);

		//Sort the list
		const sortedTouchpoints = touchpoints?.sort((a, b) => b.value - a.value);

		//Select the N first elements
		return sortedTouchpoints?.slice(0, props.n);
	}
);

export const topFiveDataSelector = createSelector(
	(state) => ({
		selectedCjms: state.analytics?.journeyTrend?.selectedFilterOptions?.cjms,
		opportunities: state.analytics?.journeyTrend?.currentData?.opportunities,
		solutions: state.analytics?.journeyTrend?.currentData?.solutions,
	}),
	(state, props) => props,
	({ selectedCjms, opportunities, solutions }, props) => {
		const { entity } = props;
		const records = entity === "opportunity" ? opportunities : solutions;
		const result = new Map();

		for (const rec of records) {
			const relatedItems = [];
			const relatedItemsMemo = {};
			const totalUsage = rec?.cjmIds?.length || 0;
			rec?.cjmIds?.forEach((cjmId) => {
				if (!(cjmId in relatedItemsMemo)) {
					relatedItemsMemo[cjmId] = true;
					const cjm = selectedCjms?.find((selectedCjm) => selectedCjm?.id === cjmId);
					if (cjm) relatedItems.push(cjm);
				}
			});

			result.set(rec.id, {
				id: rec.id,
				name: rec.name || "NEW_ITEM",
				value: totalUsage,
				relatedItems,
			});
		}

		const recList = Array.from(result.values()).sort((a, b) => b.value - a.value);
		return recList.slice(0, 5);
	}
);
